import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Layout/Sidebar';
import ClientService from '../../services/clientService';
import { FiArrowLeft, FiUser, FiMail, FiPhone, FiMapPin } from 'react-icons/fi';

export default function ClientEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState({ name: '', company:'', email: '', phone: '', address: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClient = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await ClientService.getClientById(id);
        
        if (response?.data?.client) {
          setClient({
            name: response.data.client.name || '',
            company: response.data.client.company || '',
            email: response.data.client.email || '',
            phone: response.data.client.phone || '',
            address: response.data.client.address || '',
          });
        } else if (response?.client) {
          setClient({
            name: response.client.name || '',
            company: response.client.company || '',
            email: response.client.email || '',
            phone: response.client.phone || '',
            address: response.client.address || '',
          });
        } else {
          setError('Client data not found in response');
        }
      } catch (err) {
        console.error('Error fetching client:', err);
        setError('Failed to load client data');
      } finally {
        setLoading(false);
      }
    };
    fetchClient();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClient((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await ClientService.updateClient(id, client);
      navigate('/clients/list');
    } catch (err) {
      console.error('Error updating client:', err);
      setError('Failed to update client. Please try again.');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/clients/list');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex">
        <Sidebar />
        <div className="flex-1 md:ml-64 mt-14 md:mt-0 px-4 py-4 md:px-6 md:py-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8 flex justify-between items-center">
              <h1 className="text-3xl font-bold bg-black py-2 px-8 rounded-md">Edit Client</h1>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
              <div className="space-y-4">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-black"></div>
                <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                <div className="h-10 bg-gray-200 rounded w-full"></div>
                <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                <div className="h-10 bg-gray-200 rounded w-full"></div>
                <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                <div className="h-10 bg-gray-200 rounded w-full"></div>
                <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                <div className="h-10 bg-gray-200 rounded w-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="min-h-screen bg-gray-100 flex">
        <Sidebar />
        <div className="flex-1 md:ml-64 mt-14 md:mt-0 px-4 py-4 md:px-6 md:py-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8 flex justify-between items-center">
              <h1 className="text-3xl font-bold bg-black py-2 px-8 rounded-md">Edit Client</h1>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 text-center">
              <div className="text-red-500 text-lg font-medium mb-4">
                {error || 'Client not found'}
              </div>
              <button
                onClick={() => navigate('/clients/list')}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center mx-auto"
              >
                <FiArrowLeft className="mr-2" />
                Back to Client List
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar />
      <div className="flex-1 md:ml-64 mt-14 md:mt-0 px-4 py-4 md:px-6 md:py-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 flex justify-between items-center">
            <h1 className="text-md sm:text-3xl font-bold text-white bg-black py-2 px-8 rounded-md m-1">
              Edit Client: {client.name}
            </h1>
            <button
              onClick={() => navigate('/clients/list')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
            >
              <FiArrowLeft className="mr-2" />
              Back to List
            </button>
          </div>
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <FiUser className="mr-2 text-indigo-600" />
                Edit Client Information
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-500 flex items-center">
                  <FiUser className="mr-1" />
                  Client Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={client.name}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-500 flex items-center">
                  <FiUser className="mr-1" />
                  Company Name
                </label>
                <input
                  type="text"
                  name="company"
                  value={client.company}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-500 flex items-center">
                  <FiMail className="mr-1" />
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={client.email}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-500 flex items-center">
                  <FiPhone className="mr-1" />
                  Phone Number
                </label>
                <input
                  type="text"
                  name="phone"
                  value={client.phone}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <label className="text-sm font-medium text-gray-500 flex items-center">
                  <FiMapPin className="mr-1" />
                  Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={client.address}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="md:col-span-2 flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="animate-spin mr-2 h-5 w-5 border-t-2 border-b-2 border-white rounded-full"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}